import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SaveButton from "src/components/pages/Organize/SaveButton";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "src/components/shared/Button/Button";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import GroupsList from "src/components/shared/Grouping/GroupList/GroupsList";
import LogbooksSelector from "src/components/shared/Grouping/LogbooksSelector/LogbooksSelector";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  addElement,
  moveLogbook,
  removeElement,
  setInitialData,
  setSelectedGroup,
  setValue,
} from "src/redux/slices/organizeSlice";
import { ButtonsWrapper, Flex, PageContainer } from "src/styled";

import {
  GroupProvider,
  type IGroupContext,
} from "../../components/shared/Grouping/GroupContext";
import { type ILogbook } from "../../interfaces";
import { mapOrganize, mapToRequest } from "./Organize.utils";

const Organize = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { post, isLoading: isPostLoading } = usePostApi();

  const { groups, selectedGroup, logbooks } = useSelector(
    state => state.organizeSlice,
  );

  const { data, isLoading } = useGetApi({
    request: "get-home",
    mapper: mapOrganize,
  });

  useEffect(() => {
    if (data) {
      dispatch(setInitialData(data));
    }
  }, [data, dispatch]);

  const handleReset = async () => {
    await post({ request: "save-home-groups", body: { params: [] } });
    navigate("/home");
  };

  const handleAddElement = () =>
    dispatch(addElement({ keyForData: "groups", defaultName: "New Group" }));

  const context: IGroupContext = {
    selectedElement: selectedGroup,
    handleAddElement,
    handleReset,
    handleEditElement: (data: any) =>
      dispatch(setValue({ ...data, keyForData: "groups" })),
    handleDeleteElement: (groupId: any) =>
      dispatch(removeElement({ id: groupId, keyForData: "groups" })),
    handleSelectElement: (group: any) => dispatch(setSelectedGroup(group)),
    handleMoveLogbook: (logbook: ILogbook) =>
      dispatch(moveLogbook({ logbook, keyForData: "groups" })),
    elementsSection: {
      title: "Groups",
      emptyMessage: "No groups yet",
    },
    addButtonLabel: "Add Group +",
  };

  const handleSave = async () => {
    await post({
      request: "save-home-groups",
      body: { params: mapToRequest(groups, logbooks) },
    });
    navigate("/home");
  };

  return (
    <GroupProvider context={context}>
      <PageContainer>
        <Breadcrumbs>
          <ButtonsWrapper>
            <ConfirmationButton
              modalText="Are you sure you want to reset, current groups will be removed."
              onConfirm={handleReset}>
              Reset
            </ConfirmationButton>
            <Button onClick={handleAddElement}>{context.addButtonLabel}</Button>
            <SaveButton handleSave={handleSave} isLoading={isPostLoading} />
          </ButtonsWrapper>
        </Breadcrumbs>
        {isLoading ? (
          <Loader />
        ) : (
          <Flex mt={30}>
            <GroupsList elements={groups} />
            <LogbooksSelector />
          </Flex>
        )}
      </PageContainer>
    </GroupProvider>
  );
};

export default Organize;
